import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingWrapper, WhiteContainer } from "@components";
import { Box, Checkbox, Divider } from "@mui/material";
import { HOUSE_RULES } from "../FreemiumOnboarding/constants";
import { CollapsibleAmenities } from "@components/CollapsibleAmenities";
import { LocationPicker, type LatLng } from "@Common/LocationPicker";
import { VerificationSection } from "@components/PermitVerification/VerificationSection";
import { toTimeInputValue } from "@utils";

import AirConditioningIcon from "@assets/svg/AirConditioningIcon.svg?react";
import TvIcon from "@assets/svg/TvIcon.svg?react";
import SuppliesIcon from "@assets/svg/SuppliesIcon.svg?react";
import KitchenIcon from "@assets/svg/KitchenIcon.svg?react";
import DryerIcon from "@assets/svg/DryerIcon.svg?react";
import ClockIcon from "@assets/svg/ClockIcon.svg?react";

import ImageGalleryModal from "./components/ImageGalleryModal";
import FeesSection from "./components/FeesSection";
import DiscountSection from "./components/DiscountSection";
import AvailabilitySettingsSection from "./components/AvailabilitySettingsSection";
import ImageGridSkeleton from "./components/ImageGridSkeleton";
import { useForm, FormProvider } from "react-hook-form";

import {
  HeaderContainerGeneralSettings,
  WhiteContainerWrapper,
  HeaderTitleGeneralSettings,
  HeaderDescription,
  TabsContainer,
  TabButton,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  EditButton,
  FormContainer,
  FormLabel,
  FormInput,
  FormDivider,
  TimeIcon,
  TimeInput,
  FacilitiesContainer,
  FacilityItem,
  FacilityIcon,
  FacilityText,
  CancelButton,
  TimeInputContainerCheckIn,
  TimeInputContainerCheckOut,
  ServiceIconStyled,
  ButtonContainerStyled,
  PropertyImageStyled,
  ErrorMessageStyled,
  AmenitiesSectionStyled,
  AmenitiesContainerStyled,
  AmenityItemStyled,
  AmenityButtonStyled,
  AmenitiesDescriptionStyled,
  AmenitiesListContainerStyled,
  AmenityRowStyled,
  AmenityNameStyled,
  AmenitiesErrorStyled,
  FormTextArea,
} from "./styles";
import { usePropertySetting } from "../../services/usePropertySetting";
import { useToast } from "@context/ToastContext";

import {
  IPropertyBasicInfo,
  IPropertyFees,
  IPropertyDiscounts,
  IPropertyAmenities,
  IPropertyAvailabilitySettings,
} from "./types";

// Tab constants following TypeScript best practices
const TAB_KEYS = {
  PROPERTY_DETAILS: 'property-details',
  PRICING: 'pricing',
  AVAILABILITY_SETTINGS: 'availability-settings'
} as const;

type TabKey = typeof TAB_KEYS[keyof typeof TAB_KEYS];

//

const GeneraSetting = () => {
  // Helper function to format time to HH:mm format
  const formatTime = (time: string | undefined): string => {
    if (!time) return "";

    // If time is already in HH:mm format, return as is
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    // If time is in other formats, try to parse and format
    try {
      // Handle various time formats
      let hours = 0;
      let minutes = 0;

      if (time.includes(":")) {
        const parts = time.split(":");
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      } else if (time.includes(".")) {
        const parts = time.split(".");
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      } else {
        // Assume it's a number representing minutes since midnight
        const totalMinutes = parseInt(time, 10);
        hours = Math.floor(totalMinutes / 60);
        minutes = totalMinutes % 60;
      }

      // Ensure hours and minutes are within valid ranges
      hours = Math.max(0, Math.min(23, hours));
      minutes = Math.max(0, Math.min(59, minutes));

      // Format to HH:mm
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    } catch (error) {
      return time; // Return original if formatting fails
    }
  };

  // hooks
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // state
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.PROPERTY_DETAILS);
  const [open, setOpen] = useState(false);
  const [editAmenities, setEditAmenities] = useState(false);
  const [editPropertyInformation, setEditPropertyInformation] = useState(false);
  const [editHouseRules, setEditHouseRules] = useState(false);
  const [houseRuleKeys, setHouseRuleKeys] = useState<string[]>([]);
  const [selectedAmenitiesParent, setSelectedAmenitiesParent] = useState("");
  const [selectedAmenitiesChild, setSelectedAmenitiesChild] = useState([]);

  // API hooks
  const {
    GetAmenitiesQuery,
    GetPropertyManagementQuery,
    GetPropertyImagesQuery,
    GetAvailabilityRuleSettingQuery,
    UpdatePropertyBasicInfoMutation,
    UpdatePropertyFeesMutation,
    UpdatePropertyDiscountsMutation,
    UpdatePropertyAmenitiesMutation,
    UpdatePropertyHouseRulesMutation,
    UpdatePropertyAvailabilitySettingsMutation,
  } = usePropertySetting();

  // queries
  const { data: amenities, isLoading: amenitiesLoading } = GetAmenitiesQuery();

  // Map amenity key -> localized name from the shared backend source, so the
  // view shows the same labels as onboarding / add-property (not raw keys).
  const amenityNameByKey = useMemo(() => {
    const map: Record<string, string> = {};
    amenities?.data?.result?.forEach((cat: any) =>
      cat?.amenities?.forEach((a: any) => {
        if (a?.key) map[a.key] = a.name;
      })
    );
    return map;
  }, [amenities]);
  const {
    data: propertyManagement,
    isLoading: propertyManagementLoading,
    refetch: refetchPropertyManagement,
    isRefetching: isRefetchingPropertyManagement,
  } = GetPropertyManagementQuery(id || "");

  // Availability settings query - only enabled when the availability tab is active
  const {
    data: availabilityRuleSettings,
    isLoading: isLoadingAvailabilitySettings,
    refetch: refetchAvailabilitySettings,
  } = GetAvailabilityRuleSettingQuery(id || "", activeTab === TAB_KEYS.AVAILABILITY_SETTINGS);
  const { data: propertyImages, isLoading: propertyImagesLoading } =
    GetPropertyImagesQuery(id || "");

  const { mutate: updatePropertyBasicInfo, isPending: isUpdatingBasicInfo } =
    UpdatePropertyBasicInfoMutation();

  const { mutate: updatePropertyFees, isPending: isUpdatingFees } =
    UpdatePropertyFeesMutation();
  const { mutate: updatePropertyDiscounts, isPending: isUpdatingDiscounts } =
    UpdatePropertyDiscountsMutation();
  const { mutate: updatePropertyAmenities, isPending: isUpdatingAmenities } =
    UpdatePropertyAmenitiesMutation();
  const { mutate: updatePropertyHouseRules, isPending: isUpdatingHouseRules } =
    UpdatePropertyHouseRulesMutation();

  // Availability settings mutation
  const { mutate: updatePropertyAvailabilitySettings, isPending: isUpdatingAvailabilitySettings } =
    UpdatePropertyAvailabilitySettingsMutation();

  // memo
  const propertyInformationData = useMemo(() => {
    const result = propertyManagement?.data?.result;
    setSelectedAmenitiesChild(result?.amenities?.map((item: any) => item?.key));

    return {
      id: result?.id,
      name: result?.name,
      description: result?.description,
      guestCount: result?.gusetCount,
      wifiName: result?.wifiName,
      wifiPassword: result?.wifiPassWord,
      checkInTime: result?.checkInTime,
      checkOutTime: result?.checkOutTime,
      roomCount: result?.roomCount,
      bedCount: result?.bedCount,
      bathroomCount: result?.bathroomCount,
      cleaningFee: result?.cleaningFee,
      managementFee: result?.managmentFee,
      communityFee: result?.communityFee,
      resortFee: result?.resortFee,
      laundryFees: result?.laundryFees,
      propertiesId: result?.propertiesId,
      images: result?.images,
      amenities: result?.amenities,
      houseRules: result?.houseRules,
      latitude: result?.latitude,
      longitude: result?.longitude,
      location: result?.location,
      channels: result?.channels,
      weeklyDiscount: result?.weeklyDiscount,
      monthlyDiscount: result?.monthlyDiscount,
      airbnbCommission: result?.airbnbCommission,
      gathernCommission: result?.gathernCommission,
      bookingCommission: result?.bookingCommission,
      msoolCommission: result?.msoolCommission,
      // Availability settings fields
      max_days_notice: result?.max_days_notice,
      booking_lead_time: result?.booking_lead_time,
      turnover_days: result?.turnover_days,
      default_max_nights: result?.default_max_nights,
      default_min_nights: result?.default_min_nights,
      day_of_week_check_in: result?.day_of_week_check_in,
      day_of_week_check_out: result?.day_of_week_check_out,
      day_of_week_min_nights: result?.day_of_week_min_nights,
      // MT permit verification (MS-488) — read defensively; absent until the
      // backend exposes them on the property details response.
      permitVerificationStatus: result?.permitVerificationStatus,
      permitNumber: result?.permitNumber,
      permitExpiryDate: result?.permitExpiryDate,
      permitOwnerNameAr: result?.ownerNameAr ?? result?.permitOwnerNameAr,
      permitOwnerNameEn: result?.ownerNameEn ?? result?.permitOwnerNameEn,
      permitMaskedOwnerId: result?.maskedOwnerId ?? result?.permitMaskedOwnerId,
      permitOwnerIdentityId: result?.ownerIdentityId ?? result?.permitOwnerIdentityId,
    };
  }, [propertyManagement]);

  // The property's saved house rules (comma-joined keys) resolved against the
  // shared HOUSE_RULES list — same source/labels as onboarding / add-property.
  const selectedHouseRules = useMemo(() => {
    const raw = propertyInformationData?.houseRules;
    if (!raw) return [];
    const keys = String(raw)
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    return HOUSE_RULES.filter((r) => keys.includes(r.key));
  }, [propertyInformationData?.houseRules]);

  // Map pin — seeded from the saved property, editable via the picker.
  const [coords, setCoords] = useState<LatLng | null>(null);
  useEffect(() => {
    const lat = propertyInformationData?.latitude;
    const lng = propertyInformationData?.longitude;
    setCoords(lat != null && lng != null ? { latitude: lat, longitude: lng } : null);
  }, [propertyInformationData?.latitude, propertyInformationData?.longitude]);
  // Editable location label for the merged field — seeded from the saved property
  // (Properties.PropertyLocation) and persisted back on save via UpdateBasicInfo.
  const [locationText, setLocationText] = useState("");
  useEffect(() => {
    setLocationText(propertyInformationData?.location ?? "");
  }, [propertyInformationData?.location]);

  // Update form values when propertyInformationData changes
  useEffect(() => {
    setInitialFormValues();
  }, [propertyInformationData]);

  // Update availability settings form when API data is loaded
  useEffect(() => {
    setAvailabilityFormValues();
  }, [availabilityRuleSettings, propertyInformationData]);

  // React Hook Form setup for basic info
  const {
    register: registerBasicInfo,
    control: controlBasicInfo,
    handleSubmit: handleSubmitBasicInfo,
    setValue: setValueBasicInfo,
    watch: watchBasicInfo,
    reset: resetBasicInfo,
    trigger: triggerBasicInfo,
    formState: { errors: errorsBasicInfo },
  } = useForm<IPropertyBasicInfo>({
    defaultValues: {
      id: 0,
      name: "",
      description: "",
      wifiName: "",
      wifiPassWord: "",
      gusetCount: 0,
      roomCount: 0,
      bedCount: 0,
      bathroomCount: 0,
      checkInTime: "",
      checkOutTime: "",
    },
  });

  // Debug: Log current form values
  useEffect(() => {
    const subscription = watchBasicInfo((value, { name, type }) => {});
    return () => subscription.unsubscribe();
  }, [watchBasicInfo]);


  // React Hook Form setup for fees
  const {
    register: registerFees,
    control: controlFees,
    handleSubmit: handleSubmitFees,
    setValue: setValueFees,
    watch: watchFees,
    reset: resetFees,
    trigger: triggerFees,
    formState: { errors: errorsFees },
  } = useForm<IPropertyFees>({
    defaultValues: {
      id: 0,
      cleaningFee: 0,
      managmentFee: 0,
      communityFee: 0,
      resortFee: 0,
      laundryFees: 0,
    },
  });

  // React Hook Form setup for discounts
  const {
    register: registerDiscounts,
    control: controlDiscounts,
    handleSubmit: handleSubmitDiscounts,
    setValue: setValueDiscounts,
    watch: watchDiscounts,
    reset: resetDiscounts,
    trigger: triggerDiscounts,
    formState: { errors: errorsDiscounts },
  } = useForm<IPropertyDiscounts>({
    defaultValues: {
      id: 0,
      monthlyDiscount: 0,
      weeklyDiscount: 0,
    },
  });

  // React Hook Form setup for amenities
  const {
    register: registerAmenities,
    control: controlAmenities,
    handleSubmit: handleSubmitAmenities,
    setValue: setValueAmenities,
    watch: watchAmenities,
    reset: resetAmenities,
    trigger: triggerAmenities,
    formState: { errors: errorsAmenities },
  } = useForm<IPropertyAmenities>({
    defaultValues: {
      id: 0,
      amenities: [],
    },
  });

  // React Hook Form setup for availability settings
  const availabilitySettingsMethods = useForm<IPropertyAvailabilitySettings>({
    defaultValues: {
      id: 0,
      max_days_notice: -1, // Default to "All Dates Available"
      booking_lead_time: 1, // Default to 1 hour
      turnover_days: 0, // Default to 0 days
      default_max_nights: 1, // Default to 1 night
      default_min_nights: 1, // Default to 1 night
      day_of_week_check_in: Array(7).fill(false), // Default all days to false
      day_of_week_check_out: Array(7).fill(false), // Default all days to false
      day_of_week_min_nights: Array(7).fill(0), // Default all days to 0
    },
  });

  const {
    register: registerAvailabilitySettings,
    control: controlAvailabilitySettings,
    handleSubmit: handleSubmitAvailabilitySettings,
    setValue: setValueAvailabilitySettings,
    watch: watchAvailabilitySettings,
    reset: resetAvailabilitySettings,
    trigger: triggerAvailabilitySettings,
    formState: { errors: errorsAvailabilitySettings },
  } = availabilitySettingsMethods;

  const setInitialFormValues = () => {
    if (propertyInformationData) {
      resetBasicInfo({
        id: propertyInformationData.id,
        name: propertyInformationData.name,
        description: propertyInformationData.description,
        wifiName: propertyInformationData.wifiName,
        wifiPassWord: propertyInformationData.wifiPassword,
        gusetCount: propertyInformationData.guestCount,
        roomCount: propertyInformationData.roomCount,
        bedCount: propertyInformationData.bedCount,
        bathroomCount: propertyInformationData.bathroomCount,
        checkInTime: toTimeInputValue(propertyInformationData.checkInTime),
        checkOutTime: toTimeInputValue(propertyInformationData.checkOutTime),
      });

      // Set fees form values
      resetFees({
        id: propertyInformationData.id,
        cleaningFee: propertyInformationData?.cleaningFee || 0,
        managmentFee: propertyInformationData?.managementFee || 0,
        communityFee: propertyInformationData?.communityFee || 0,
        resortFee: propertyInformationData?.resortFee || 0,
        laundryFees: propertyInformationData?.laundryFees || 0,
      });

      // Set amenities form values
      resetAmenities({
        id: propertyInformationData.id,
        amenities: propertyInformationData?.amenities || [],
      });
    }
  };

  // Separate function to set availability settings form values
  const setAvailabilityFormValues = () => {
    if (availabilityRuleSettings?.data?.result && propertyInformationData?.id) {
      const result = availabilityRuleSettings.data.result;
      resetAvailabilitySettings({
        id: propertyInformationData.id,
        max_days_notice: result.max_days_notice ?? -1,
        booking_lead_time: result.booking_lead_time ?? 1,
        turnover_days: result.turnover_days ?? 0,
        default_max_nights: result.default_max_nights ?? 1,
        default_min_nights: result.default_min_nights ?? 1,
        day_of_week_check_in: result.day_of_week_check_in ?? Array(7).fill(false),
        day_of_week_check_out: result.day_of_week_check_out ?? Array(7).fill(false),
        day_of_week_min_nights: result.day_of_week_min_nights ?? Array(7).fill(0),
      });
    }
  };

  const onSubmitBasicInfo = handleSubmitBasicInfo(async (data) => {
    // Merge the location label + map pin into the basic-info update (persisted
    // server-side: label → Properties.PropertyLocation, pin → PropertyLocation table).
    updatePropertyBasicInfo({ ...data, location: locationText, latitude: coords?.latitude, longitude: coords?.longitude }, {
      onSuccess: () => {
        setEditPropertyInformation(false);
        setEditAmenities(false);
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitHouseRules = () => {
    const id = propertyInformationData?.id;
    if (!id) return;
    updatePropertyHouseRules(
      { id, houseRules: houseRuleKeys.join(", ") },
      {
        onSuccess: () => {
          setEditHouseRules(false);
          refetchPropertyManagement();
          showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
            severity: "success",
          });
        },
      }
    );
  };

  const onSubmitFees = handleSubmitFees(async (data) => {
    updatePropertyFees(data, {
      onSuccess: () => {
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitDiscounts = handleSubmitDiscounts(async (data) => {
    updatePropertyDiscounts(data, {
      onSuccess: () => {
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitAmenities = handleSubmitAmenities(async (data) => {
    updatePropertyAmenities(data, {
      onSuccess: () => {
        setEditAmenities(false);
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitAvailabilitySettings = handleSubmitAvailabilitySettings(async (data) => {
    updatePropertyAvailabilitySettings({ propertyId: id || "", data }, {
      onSuccess: () => {
        refetchPropertyManagement();
        refetchAvailabilitySettings();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const tabs = [
    {
      label: t("PROPERTIES_MANAGEMENT.PROPERTY_DETAILS"),
      value: TAB_KEYS.PROPERTY_DETAILS,
    },
    {
      label: t("PROPERTIES_MANAGEMENT.PRICING"),
      value: TAB_KEYS.PRICING,
    },
    {
      label: t("PROPERTIES_MANAGEMENT.AVAILABILITY_SETTINGS"),
      value: TAB_KEYS.AVAILABILITY_SETTINGS,
    },
  ];
  const servicesIcons = [
    <ServiceIconStyled>
      <AirConditioningIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <TvIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <SuppliesIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <DryerIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <KitchenIcon />
    </ServiceIconStyled>,
  ];

  return (
    <Box>
      {/* Header  */}
      <HeaderContainerGeneralSettings>
        <HeaderTitleGeneralSettings
          onClick={() => navigate("/properties-management")}
        >
          {t("PROPERTIES_MANAGEMENT.ALL_PROPERTIES")}
        </HeaderTitleGeneralSettings>
        <HeaderTitleGeneralSettings>/</HeaderTitleGeneralSettings>
        <HeaderTitleGeneralSettings>
          {propertyInformationData?.name}
        </HeaderTitleGeneralSettings>
      </HeaderContainerGeneralSettings>
      <HeaderDescription>
        {propertyInformationData?.description}
      </HeaderDescription>
      {/* Container */}
      <WhiteContainerWrapper>
        <WhiteContainer borderRadius="0" border="0" >
          {/* tabs part  */}
          <TabsContainer>
            {tabs.map((tab) => (
              <TabButton
                key={tab.value}
                active={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabsContainer>
          {activeTab === TAB_KEYS.PROPERTY_DETAILS && (
            <>
              {/* MT permit verification (MS-488) — first section, above الصور.
                  mt keeps the section title clear of the tabs (MS-501). */}
              <Box sx={{ mt: "24px", mb: "24px" }}>
                <VerificationSection
                  propertyId={Number(id)}
                  status={propertyInformationData?.permitVerificationStatus}
                  permitNumber={propertyInformationData?.permitNumber}
                  expiryDate={propertyInformationData?.permitExpiryDate}
                  ownerNameAr={propertyInformationData?.permitOwnerNameAr}
                  ownerNameEn={propertyInformationData?.permitOwnerNameEn}
                  maskedOwnerId={propertyInformationData?.permitMaskedOwnerId}
                  ownerIdentityId={propertyInformationData?.permitOwnerIdentityId}
                  onChanged={() => refetchPropertyManagement()}
                />
              </Box>
              <Divider sx={{ mb: "24px" }} />

              {/* Image Content Section  */}
              <SectionHeader>
                <SectionTitle>{t("PROPERTIES_MANAGEMENT.IMAGES")}</SectionTitle>
                <EditButton onClick={() => setOpen(true)}>
                  {t("PROPERTIES_MANAGEMENT.EDIT")}
                </EditButton>
              </SectionHeader>
              <LoadingWrapper loading={propertyManagementLoading}>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-[7px] p-6">
                  {propertyManagementLoading ? (
                    <ImageGridSkeleton count={7} />
                  ) : (
                    propertyImages?.data?.result
                      ?.filter((item: any) => item?.image)
                      ?.map((item: any) => (
                        <div
                          key={item?.id}
                          className="aspect-[4/3] relative overflow-hidden rounded-lg"
                        >
                          <PropertyImageStyled
                            src={item?.image}
                            alt="property"
                          />
                        </div>
                      ))
                  )}
                </div>
              </LoadingWrapper>
              <FormDivider />
              {/* Property Information Section  */}
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.PROPERTY_INFORMATION")}
                </SectionTitle>
                {!editPropertyInformation && (
                  <EditButton onClick={() => setEditPropertyInformation(true)}>
                    {t("PROPERTIES_MANAGEMENT.EDIT")}
                  </EditButton>
                )}
                {editPropertyInformation && (
                  <ButtonContainerStyled>
                    <EditButton
                      onClick={() => {
                        setEditPropertyInformation(false);
                        setInitialFormValues();
                      }}
                    >
                      {t("COMMON.CANCEL")}
                    </EditButton>
                    <CancelButton
                      onClick={() => {
                        onSubmitBasicInfo();
                      }}
                    >
                      {t("COMMON.SAVE")}
                    </CancelButton>
                  </ButtonContainerStyled>
                )}
              </SectionHeader>
              {/* form section  */}
              <LoadingWrapper loading={isUpdatingBasicInfo}>
                <FormContainer>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.PROPERTY_NAME")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("name", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("name") || ""}
                  />
                  {errorsBasicInfo.name && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.name.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.DESCRIPTION")}
                  </FormLabel>
                  <FormTextArea
                    disabled={!editPropertyInformation}
                    {...registerBasicInfo("description", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("description") || ""}
                  />
                  {errorsBasicInfo.description && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.description.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.INTERNET_CONNECTION")}
                  </SectionSubtitle>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.WIFI_NETWORK_NAME")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("wifiName")}
                    value={watchBasicInfo("wifiName") || ""}
                  />
                  {errorsBasicInfo.wifiName && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.wifiName.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.WIFI_PASSWORD")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("wifiPassWord")}
                    value={watchBasicInfo("wifiPassWord") || ""}
                  />
                  {errorsBasicInfo.wifiPassWord && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.wifiPassWord.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.ROOMS")}
                  </SectionSubtitle>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_GUESTS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("gusetCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t(
                          "PROPERTIES_MANAGEMENT.GUEST_COUNT_MIN_VALUE"
                        ),
                      },
                    })}
                    value={watchBasicInfo("gusetCount") || 0}
                  />
                  {errorsBasicInfo.gusetCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.gusetCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_ROOMS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("roomCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t(
                          "PROPERTIES_MANAGEMENT.ROOM_COUNT_MIN_VALUE"
                        ),
                      },
                    })}
                    value={watchBasicInfo("roomCount") || 0}
                  />
                  {errorsBasicInfo.roomCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.roomCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_BEDS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("bedCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t("PROPERTIES_MANAGEMENT.BED_COUNT_MIN_VALUE"),
                      },
                    })}
                    value={watchBasicInfo("bedCount") || 0}
                  />
                  {errorsBasicInfo.bedCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.bedCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_BATHROOMS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("bathroomCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t(
                          "PROPERTIES_MANAGEMENT.BATHROOM_COUNT_MIN_VALUE"
                        ),
                      },
                    })}
                    value={watchBasicInfo("bathroomCount") || 0}
                  />
                  {errorsBasicInfo.bathroomCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.bathroomCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.BOOKING_POLICY")}
                  </SectionSubtitle>
                  <FormLabel>{t("PROPERTIES_MANAGEMENT.CHECK_IN")}</FormLabel>
                  <TimeInputContainerCheckIn>
                    {!editPropertyInformation && (
                      <TimeIcon>
                        <ClockIcon />
                      </TimeIcon>
                    )}
                    <TimeInput
                      {...registerBasicInfo("checkInTime")}
                      type="time"
                      disabled={!editPropertyInformation}
                      value={toTimeInputValue(watchBasicInfo("checkInTime"))}
                      onChange={(e) => {
                        // Native input already emits 24h "HH:mm" — the format the
                        // basic-info endpoint stores; keep it as-is.
                        setValueBasicInfo("checkInTime", e.target.value);
                      }}
                    />
                  </TimeInputContainerCheckIn>
                  <FormLabel>{t("PROPERTIES_MANAGEMENT.CHECK_OUT")}</FormLabel>
                  <TimeInputContainerCheckOut>
                    {!editPropertyInformation && (
                      <TimeIcon>
                        <ClockIcon />
                      </TimeIcon>
                    )}
                    <TimeInput
                      {...registerBasicInfo("checkOutTime")}
                      type="time"
                      disabled={!editPropertyInformation}
                      value={toTimeInputValue(watchBasicInfo("checkOutTime"))}
                      onChange={(e) => {
                        setValueBasicInfo("checkOutTime", e.target.value);
                      }}
                    />
                  </TimeInputContainerCheckOut>
                  <Box sx={{ mt: "12px" }}>
                    <Box sx={{ fontSize: 14, fontWeight: 600, color: "#435363", mb: "6px" }}>
                      {t("PROPERTIES_MANAGEMENT.LOCATION_ON_MAP")}
                    </Box>
                    <LocationPicker
                      value={{
                        address: locationText,
                        latitude: coords?.latitude ?? null,
                        longitude: coords?.longitude ?? null,
                      }}
                      onChange={(v) => {
                        setLocationText(v.address);
                        setCoords(
                          v.latitude != null && v.longitude != null
                            ? { latitude: v.latitude, longitude: v.longitude }
                            : null
                        );
                      }}
                      height={260}
                      disabled={!editPropertyInformation}
                    />
                  </Box>
                  <FormDivider />
                </FormContainer>
              </LoadingWrapper>
              {/* Facilities and supplies section  */}
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.FACILITIES_AND_SUPPLIES")}
                </SectionTitle>
                {!editAmenities && (
                  <EditButton onClick={() => setEditAmenities(true)}>
                    {t("PROPERTIES_MANAGEMENT.EDIT")}
                  </EditButton>
                )}
                {editAmenities && (
                  <ButtonContainerStyled>
                    <EditButton
                      onClick={() => {
                        setInitialFormValues();
                        setEditAmenities(false);
                      }}
                    >
                      {t("COMMON.CANCEL")}
                    </EditButton>
                    <CancelButton
                      onClick={() => {
                        // Check if amenities are selected
                        if (
                          selectedAmenitiesChild.length ===
                          watchAmenities("amenities").length
                        ) {
                          return;
                        }
                        onSubmitAmenities();
                      }}
                    >
                      {t("COMMON.SAVE")}
                    </CancelButton>
                  </ButtonContainerStyled>
                )}
              </SectionHeader>
              {/* List of facilities and supplies  */}
              <LoadingWrapper loading={isUpdatingAmenities}>
                {editAmenities && (
                  <AmenitiesSectionStyled>
                    <CollapsibleAmenities
                      categories={amenities?.data?.result ?? []}
                      isSelected={(key) =>
                        (watchAmenities("amenities") || []).some(
                          (a: any) => a.key === key
                        )
                      }
                      onToggle={(item) => {
                        const current = watchAmenities("amenities") || [];
                        const exists = current.some(
                          (a: any) => a.key === item.key
                        );
                        setValueAmenities(
                          "amenities",
                          exists
                            ? current.filter(
                                (a: any) => a.key !== item.key
                              )
                            : [
                                ...current,
                                {
                                  name: item.name,
                                  key: item.key,
                                  isActive: true,
                                },
                              ]
                        );
                      }}
                    />

                    {/* Error message for no amenities selected */}
                    {selectedAmenitiesChild.length ===
                      watchAmenities("amenities").length && (
                      <AmenitiesErrorStyled isRTL={isRTL}>
                        {t("PROPERTIES_MANAGEMENT.AMENITIES_REQUIRED")}
                      </AmenitiesErrorStyled>
                    )}
                  </AmenitiesSectionStyled>
                )}
              </LoadingWrapper>

              {!editAmenities && (
                <FacilitiesContainer>
                  {propertyInformationData?.amenities?.map((service: any) => (
                    <FacilityItem key={service.key}>
                      <FacilityIcon>
                        <DryerIcon />
                      </FacilityIcon>
                      <FacilityText>
                        {amenityNameByKey[service.key] || service.name}
                      </FacilityText>
                    </FacilityItem>
                  ))}
                </FacilitiesContainer>
              )}

              {/* House Rules section */}
              <FormDivider />
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.HOUSE_RULES")}
                </SectionTitle>
                {!editHouseRules && (
                  <EditButton
                    onClick={() => {
                      setHouseRuleKeys(selectedHouseRules.map((r) => r.key));
                      setEditHouseRules(true);
                    }}
                  >
                    {t("PROPERTIES_MANAGEMENT.EDIT")}
                  </EditButton>
                )}
                {editHouseRules && (
                  <ButtonContainerStyled>
                    <EditButton onClick={() => setEditHouseRules(false)}>
                      {t("COMMON.CANCEL")}
                    </EditButton>
                    <CancelButton onClick={onSubmitHouseRules}>
                      {t("COMMON.SAVE")}
                    </CancelButton>
                  </ButtonContainerStyled>
                )}
              </SectionHeader>
              <LoadingWrapper loading={isUpdatingHouseRules}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {editHouseRules ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {HOUSE_RULES.map((rule) => {
                        const active = houseRuleKeys.includes(rule.key);
                        return (
                          <Box
                            key={rule.key}
                            onClick={() =>
                              setHouseRuleKeys((prev) =>
                                prev.includes(rule.key)
                                  ? prev.filter((k) => k !== rule.key)
                                  : [...prev, rule.key]
                              )
                            }
                            sx={{
                              cursor: 'pointer',
                              userSelect: 'none',
                              background: active ? '#5B3FFF' : '#FFFFFF',
                              color: active ? '#FFFFFF' : '#334155',
                              border: active
                                ? '1px solid #5B3FFF'
                                : '1px solid #E2E8F0',
                              borderRadius: '8px',
                              px: '12px',
                              py: '6px',
                              fontSize: '13px',
                              fontWeight: 500,
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {t(rule.labelKey)}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : selectedHouseRules.length ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedHouseRules.map((rule) => (
                        <Box
                          key={rule.key}
                          sx={{
                            background: '#F1F5F9',
                            color: '#334155',
                            borderRadius: '8px',
                            px: '12px',
                            py: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                          }}
                        >
                          {t(rule.labelKey)}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <SectionSubtitle sx={{ fontWeight: 400, fontSize: '13px', color: '#475569', lineHeight: 1.7 }}>
                      {t("PROPERTIES_MANAGEMENT.NO_HOUSE_RULES")}
                    </SectionSubtitle>
                  )}
                </Box>
              </LoadingWrapper>
            </>
          )}
          {activeTab === TAB_KEYS.PRICING && (
            <>
              {/* form section  */}

              <LoadingWrapper loading={isUpdatingFees}>
                <FormContainer>
                  <FeesSection
                    reset={setInitialFormValues}
                    fees={{
                      cleaningFee: propertyInformationData?.cleaningFee,
                      managementFee: propertyInformationData?.managementFee,
                      communityFee: propertyInformationData?.communityFee,
                      resortFee: propertyInformationData?.resortFee,
                      laundryFees: propertyInformationData?.laundryFees,
                    }}
                    register={registerFees}
                    control={controlFees}
                    trigger={triggerFees}
                    onSave={onSubmitFees}
                  />
                </FormContainer>
              </LoadingWrapper>

              <FormDivider />

              <LoadingWrapper loading={isUpdatingDiscounts}>
                <FormContainer>
                  <DiscountSection
                    control={controlDiscounts}
                    reset={setInitialFormValues}
                    register={registerDiscounts}
                    trigger={triggerDiscounts}
                    onSave={onSubmitDiscounts}
                    discounts={{
                      weekly: propertyInformationData?.weeklyDiscount,
                      monthly: propertyInformationData?.monthlyDiscount,
                    }}
                  />
                </FormContainer>
              </LoadingWrapper>
            </>
          )}
          {activeTab === TAB_KEYS.AVAILABILITY_SETTINGS && (
            <>
              {/* availability settings tab  */}
              <LoadingWrapper loading={isUpdatingAvailabilitySettings || isLoadingAvailabilitySettings}>
                <FormContainer>
                  <FormProvider {...availabilitySettingsMethods}>
                    <AvailabilitySettingsSection
                      availabilitySettings={{
                        max_days_notice: availabilityRuleSettings?.data?.result?.max_days_notice,
                        booking_lead_time: availabilityRuleSettings?.data?.result?.booking_lead_time,
                        turnover_days: availabilityRuleSettings?.data?.result?.turnover_days,
                        default_max_nights: availabilityRuleSettings?.data?.result?.default_max_nights,
                        default_min_nights: availabilityRuleSettings?.data?.result?.default_min_nights,
                        day_of_week_check_in: availabilityRuleSettings?.data?.result?.day_of_week_check_in,
                        day_of_week_check_out: availabilityRuleSettings?.data?.result?.day_of_week_check_out,
                        day_of_week_min_nights: availabilityRuleSettings?.data?.result?.day_of_week_min_nights,
                      }}
                      register={registerAvailabilitySettings}
                      control={controlAvailabilitySettings}
                      onSave={onSubmitAvailabilitySettings}
                      reset={setInitialFormValues}
                      trigger={triggerAvailabilitySettings}
                    />
                  </FormProvider>
                </FormContainer>
              </LoadingWrapper>
            </>
          )}
        </WhiteContainer>
      </WhiteContainerWrapper>
      {/* Image Gallery Modal */}
      <ImageGalleryModal
        open={open}
        onClose={() => setOpen(false)}
        isRTL={isRTL}
        propertyId={id}
      />
      {/* Time Selection Modal */}
    </Box>
  );
};

export default GeneraSetting;
